import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Mountain,
  Map,
  Layers,
  BookOpen,
  TrendingUp,
  Target,
  Zap,
  ArrowRight,
  ChevronRight,
  Settings,
  SearchCode,
  Clock,
  Award,
  Activity,
  Lightbulb,
  Play,
  Boxes,
  Code,
  Palette,
  FlaskConical,
  FileText,
  Sliders,
  GitBranch,
  FileEdit,
  Tag,
  PenTool,
  Package,
  Globe,
  Workflow,
  Library,
  Users,
} from 'lucide-react';
import { journeyTracker } from '../lib/journeyTracker';
import type { UserJourney } from '@/types/learning';

type VisitedPage = { path: string; timestamp: number | string | Date };
type JourneyWithVisits = UserJourney & { visitedPages?: VisitedPage[] };
import { outcomeTracker } from '../lib/outcomeTracker';
import { YourRankBadge } from '../components/team-widgets/YourRankBadge';
import { masterRegistryEntries } from '../data/masterRegistryEntries';
import { designTimeTemplates } from '../data/designTimeExamples';
import { allOperations } from '../data/operations';

// ── Shared section header ────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, label, title, description }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="w-8 h-8 bg-primary-50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="h-4 w-4 text-primary-600" />
      </div>
      <div>
        <p className="text-xs font-mono uppercase tracking-wider text-[var(--p-faint)] mb-0.5">{label}</p>
        <h2 className="text-xl font-heading font-semibold text-[var(--p-ink)]">{title}</h2>
        {description && <p className="text-sm text-[var(--p-ink-soft)] mt-1 leading-relaxed">{description}</p>}
      </div>
    </div>
  );
}

export const MissionControl: React.FC = () => {
  const [journey, setJourney] = useState<JourneyWithVisits | null>(null);
  const [hasVisited, setHasVisited] = useState(false);

  useEffect(() => {
    const currentJourney = journeyTracker.getJourney();
    setJourney(currentJourney);
    const visitedPages = (currentJourney as JourneyWithVisits).visitedPages;
    setHasVisited(!!visitedPages && visitedPages.length > 1);
  }, []);

  const quickStats = [
    { label: 'Constructs (M2)', value: masterRegistryEntries.length, icon: GitBranch, path: '/elevation/models' },
    { label: 'Templates (M1)', value: designTimeTemplates.length, icon: FileEdit, path: '/elevation/templates' },
    { label: 'Records (M0)', value: 0, icon: Zap, path: '/elevation/runtime' },
    { label: 'Operations', value: allOperations.length, icon: Code, path: '/elevation/operations' },
  ];

  const workflowSteps = [
    { name: 'Gathering', description: 'Collecting ideas, content, and insights from diverse sources', engine: 'Prospector Engine', icon: SearchCode, path: '/elevation/operations' },
    { name: 'Tagging', description: 'Adding metadata, categorization, and structure to collected content', engine: 'Knowledge Engine', icon: Tag, path: '/elevation/operations' },
    { name: 'Formulating', description: 'Research, refinement, and development of ideas into coherent frameworks', engine: 'Curation Engine · Canon Engine', icon: BookOpen, path: '/elevation/operations' },
    { name: 'Creating', description: 'Authoring content and designing visual assets for maximum impact', engine: 'Authoring Engine · Studio Engine', icon: PenTool, path: '/elevation/operations' },
    { name: 'Applying', description: 'Composing content with visuals into coherent, deliverable products', engine: 'Assembly Engine', icon: Package, path: '/elevation/operations' },
    { name: 'Publishing', description: 'Publishing content across channels to create value and generate revenue', engine: 'Publishing Engine', icon: Globe, path: '/elevation/operations' },
  ];

  const mofLayers = [
    { level: 'M3', name: 'Metadata', description: 'The universal grammar for representing all knowledge. Defines the fields and structure for all models.', components: ['Ontology', 'Modality', 'Dynamics', 'Evaluation'], icon: Layers, path: '/elevation/grammar' },
    { level: 'M2', name: 'Models', description: 'Concrete frameworks like DEGAP, Johari Window, Logical Levels. Define structure, relationships, and rules.', components: ['Decision Models', 'Assessment Models', 'Process Models'], icon: GitBranch, path: '/elevation/models' },
    { level: 'M1', name: 'Templates', description: 'Ready-to-deploy templates based on M2 models. Convert models into usable forms with specific fields and validation.', components: ['Assessment Templates', 'Workshop Templates', 'Planning Templates'], icon: FileEdit, path: '/elevation/templates' },
    { level: 'M0', name: 'Runtime', description: 'Actual experiences and instances. Real workshops, assessments, and interventions with measurable outcomes.', components: ['Live Workshops', 'Client Sessions', 'Measured Outcomes'], icon: Zap, path: '/elevation/runtime' },
  ];

  const labItems = [
    { path: '/elevation/lab/outcome-driver-generator', label: 'Outcome Driver Generator', icon: Target },
    { path: '/elevation/lab/clarity-compass', label: 'Clarity Compass', icon: SearchCode },
    { path: '/elevation/lab/tool-selector', label: 'Prioritization Tool Selector', icon: Sliders },
    { path: 'http://localhost:3102', label: 'Object Inspector', icon: SearchCode, external: true },
    { path: '/elevation/canon-engine', label: 'Canon Engine', icon: BookOpen },
    { path: '/elevation/workbook/home', label: 'DEGAP Workbook', icon: BookOpen },
  ];

  return (
    <div className="space-y-8">

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] p-10 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-50 mb-5">
          <Mountain className="h-7 w-7 text-primary-600" />
        </div>
        <h1 className="text-4xl font-heading font-semibold text-[var(--p-ink)] mb-3">Elevation</h1>
        <p className="text-base text-[var(--p-ink-soft)] max-w-2xl mx-auto leading-relaxed mb-4">
          Your central hub for navigating the knowledge system. Choose your journey below based on what you want to accomplish.
        </p>
        <p className="text-sm text-primary-700 bg-primary-50 px-4 py-2 inline-block">
          <strong>New here?</strong> Start with "Explore the Landscape" to get oriented.
        </p>
      </div>

      {/* ── Journey panel (returning users) ─────────────────────── */}
      {hasVisited && journey && (
        <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] p-6">
          <SectionHeader icon={Activity} label="Personalized" title="Your Journey" />
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="border border-[var(--p-hairline)] p-4 flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-50 flex items-center justify-center flex-shrink-0">
                <Target className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <div className="text-2xl font-heading font-semibold text-[var(--p-ink)]">
                  {journey.visitedPages ? new Set(journey.visitedPages.map((p: VisitedPage) => p.path)).size : 0}
                </div>
                <div className="text-xs text-[var(--p-ink-soft)]">Pages explored</div>
              </div>
            </div>
            <div className="border border-[var(--p-hairline)] p-4 flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-50 flex items-center justify-center flex-shrink-0">
                <Clock className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <div className="text-sm font-heading font-semibold text-[var(--p-ink)]">Recent activity</div>
                <div className="text-xs text-[var(--p-ink-soft)]">
                  {(journey.visitedPages?.length ?? 0) > 0
                    ? new Date(journey.visitedPages![journey.visitedPages!.length - 1].timestamp).toLocaleDateString()
                    : 'No activity yet'}
                </div>
                {(journey.visitedPages?.length ?? 0) > 0 && (
                  <Link to={journey.visitedPages![journey.visitedPages!.length - 1].path} className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 mt-1">
                    <Play className="h-3 w-3" /> Continue
                  </Link>
                )}
              </div>
            </div>
            <div className="border border-[var(--p-hairline)] p-4 flex items-center gap-3">
              <div className="w-9 h-9 bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-4 w-4 text-[var(--mustard)]" />
              </div>
              <div>
                <div className="text-2xl font-heading font-semibold text-[var(--p-ink)]">{outcomeTracker.getOutcomes().length}</div>
                <div className="text-xs text-[var(--p-ink-soft)]">Outcomes tracked</div>
                <Link to="/elevation/outcomes" className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 mt-1">
                  <Target className="h-3 w-3" /> Track new
                </Link>
              </div>
            </div>
          </div>
          <div className="border border-[var(--p-hairline)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-4 w-4 text-[var(--mustard)]" />
              <span className="text-sm font-heading font-semibold text-[var(--p-ink)]">Recommended next steps</span>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {journey.visitedPages?.some((p: VisitedPage) => p.path.includes('/entry/')) && (
                <Link to="/elevation/workshops" className="flex items-center gap-3 p-3 border border-[var(--p-hairline)] hover:border-primary-300 hover:bg-[var(--p-shade)] transition-colors group">
                  <Award className="h-4 w-4 text-primary-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-heading font-semibold text-[var(--p-ink)]">Try a Workshop</div>
                    <div className="text-xs text-[var(--p-ink-soft)]">Practice what you've learned</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )}
              {outcomeTracker.getOutcomes().length === 0 && (
                <Link to="/elevation/outcomes" className="flex items-center gap-3 p-3 border border-[var(--p-hairline)] hover:border-primary-300 hover:bg-[var(--p-shade)] transition-colors group">
                  <Target className="h-4 w-4 text-primary-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-heading font-semibold text-[var(--p-ink)]">Create Your First Outcome</div>
                    <div className="text-xs text-[var(--p-ink-soft)]">Start tracking real-world results</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )}
              <Link to="/elevation/team" className="flex items-center gap-3 p-3 border border-[var(--p-hairline)] hover:border-primary-300 hover:bg-[var(--p-shade)] transition-colors group">
                <Users className="h-4 w-4 text-primary-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-heading font-semibold text-[var(--p-ink)]">Explore Team Stories</div>
                  <div className="text-xs text-[var(--p-ink-soft)]">Learn from others' successes</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/elevation/progress" className="flex items-center gap-3 p-3 border border-[var(--p-hairline)] hover:border-primary-300 hover:bg-[var(--p-shade)] transition-colors group">
                <Award className="h-4 w-4 text-primary-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-heading font-semibold text-[var(--p-ink)]">View Progress Dashboard</div>
                  <div className="text-xs text-[var(--p-ink-soft)]">See your complete journey</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {hasVisited && <YourRankBadge metric="exploration" />}

      {/* ── Quick stats ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.path}
              className="bg-[var(--p-shade)] border border-[var(--p-hairline)] p-6 text-center hover:border-primary-300 transition-all group"
            >
              <Icon className="h-6 w-6 mx-auto mb-2 text-[var(--p-faint)] group-hover:text-primary-500 transition-colors" />
              <div className="text-3xl font-heading font-semibold text-[var(--p-ink)] mb-1">{stat.value}</div>
              <div className="text-xs text-[var(--p-ink-soft)]">{stat.label}</div>
              <div className="mt-2 text-xs text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                View <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* ── Content Workflow ─────────────────────────────────────── */}
      <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] p-6">
        <SectionHeader icon={Workflow} label="Platform" title="Content Workflow" description="Six stages from raw input to published output." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workflowSteps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <Link
                key={step.name}
                to={step.path}
                className="flex items-start gap-3 p-4 border border-[var(--p-hairline)] hover:border-primary-300 hover:bg-[var(--p-shade)] transition-all group"
              >
                <div className="w-8 h-8 bg-[var(--p-shade)] group-hover:bg-primary-100 flex items-center justify-center flex-shrink-0 transition-colors">
                  <Icon className="h-4 w-4 text-[var(--p-ink-soft)] group-hover:text-primary-700 transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-[var(--p-faint)]">{String(idx + 1).padStart(2, '0')}</span>
                    <h3 className="text-sm font-heading font-semibold text-[var(--p-ink)]">{step.name}</h3>
                  </div>
                  <p className="text-xs text-[var(--p-ink-soft)] mb-2 leading-relaxed">{step.description}</p>
                  <span className="text-xs font-mono text-[var(--p-faint)]">{step.engine}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-600 flex-shrink-0 mt-1 transition-colors" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Four-Layer Architecture ───────────────────────────────── */}
      <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] p-6">
        <SectionHeader icon={Layers} label="Architecture" title="Four-Layer Knowledge Architecture" description="M3–M0: From metadata grammar to runtime instances." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {mofLayers.map((layer) => {
            const Icon = layer.icon;
            return (
              <Link
                key={layer.level}
                to={layer.path}
                className="border border-[var(--p-hairline)] p-5 hover:border-primary-300 transition-all group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-mono text-primary-600 font-semibold">{layer.level}</span>
                  <div className="w-5 h-5 rounded bg-[var(--p-shade)] group-hover:bg-[var(--p-shade)] flex items-center justify-center transition-colors">
                    <Icon className="h-3 w-3 text-[var(--p-ink-soft)] group-hover:text-primary-700 transition-colors" />
                  </div>
                </div>
                <h3 className="text-sm font-heading font-semibold text-[var(--p-ink)] mb-2">{layer.name}</h3>
                <p className="text-xs text-[var(--p-ink-soft)] mb-3 leading-relaxed">{layer.description}</p>
                <div className="space-y-1 mb-3">
                  {layer.components.map((c) => (
                    <div key={c} className="text-xs text-[var(--p-faint)] bg-[var(--p-shade)] px-2 py-0.5 rounded">{c}</div>
                  ))}
                </div>
                <span className="text-xs font-medium text-primary-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Explore {layer.level} <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Core sections ────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] overflow-hidden">
          <div className="p-6 pb-3">
            <SectionHeader icon={Boxes} label="Constructor" title="Template Constructor" />
            <p className="text-sm text-[var(--p-ink-soft)] leading-relaxed">Codify existing knowledge into reusable templates that preserve meaning and enable consistent reuse.</p>
          </div>
          <div className="divide-y divide-charcoal-100">
            {[
              { to: '/elevation/grammar', label: 'Metadata Schema (M3)', desc: '/grammar' },
              { to: '/elevation/models', label: 'Construct Registry (M2)', desc: '/models' },
              { to: '/elevation/templates', label: 'Template Registry (M1)', desc: '/templates' },
            ].map((link) => (
              <Link key={link.to} to={link.to} className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-[var(--p-shade)] transition-colors group">
                <div>
                  <div className="text-sm font-medium text-[var(--p-ink)] group-hover:text-primary-700">{link.label}</div>
                  <div className="text-xs font-mono text-[var(--p-ink-soft)]">{link.desc}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-600 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] overflow-hidden">
          <div className="p-6 pb-3">
            <SectionHeader icon={Settings} label="Infrastructure" title="Platform Engines" />
            <p className="text-sm text-[var(--p-ink-soft)] leading-relaxed">Infrastructure layer that orchestrates the creation, transformation, storage, and delivery of knowledge objects.</p>
          </div>
          <div className="divide-y divide-charcoal-100">
            {[
              { to: '/elevation/engines', label: 'View All Engines', desc: '/engines' },
              { to: '/elevation/operations', label: 'View All Operations', desc: '/operations' },
            ].map((link) => (
              <Link key={link.to} to={link.to} className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-[var(--p-shade)] transition-colors group">
                <div>
                  <div className="text-sm font-medium text-[var(--p-ink)] group-hover:text-primary-700">{link.label}</div>
                  <div className="text-xs font-mono text-[var(--p-ink-soft)]">{link.desc}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-600 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── Design Language ──────────────────────────────────────── */}
      <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] p-6">
        <SectionHeader icon={Palette} label="Design" title="Design Language" description="Visual design language, components, and guidelines that ensure consistent brand expression across all materials." />
        <Link to="/elevation/design-language" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 group">
          Explore Design Language <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* ── Documentation ────────────────────────────────────────── */}
      <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] overflow-hidden">
        <div className="p-6 pb-3">
          <SectionHeader icon={Library} label="Resources" title="Documentation & Resources" />
        </div>
        <div className="divide-y divide-charcoal-100">
          {[
            { to: '/elevation/glossary', icon: BookOpen, label: 'Glossary', desc: 'Key terms and definitions' },
            { to: '/elevation/site-map', icon: Map, label: 'Site Map', desc: 'Complete site structure' },
            { to: '/elevation/documentation-hub', icon: FileText, label: 'Documentation', desc: 'System documentation' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-[var(--p-shade)] transition-colors group">
                <div className="flex items-center gap-3 min-w-0">
                  <Icon className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-700 flex-shrink-0 transition-colors" />
                  <div>
                    <div className="text-sm font-medium text-[var(--p-ink)] group-hover:text-primary-700">{item.label}</div>
                    <div className="text-xs text-[var(--p-ink-soft)]">{item.desc}</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-600 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── The Lab ──────────────────────────────────────────────── */}
      <div className="bg-[var(--p-shade)] border border-[var(--p-hairline)] overflow-hidden">
        <div className="p-6 pb-3">
          <SectionHeader icon={FlaskConical} label="Experimental" title="The Lab" description="Early access to new capabilities, development tools, and experimental methodologies." />
        </div>
        <div className="divide-y divide-charcoal-100 mb-4">
          {labItems.map((item) => {
            const Icon = item.icon;
            const rowClass = "flex items-center justify-between gap-4 px-6 py-3 hover:bg-[var(--p-shade)] transition-colors group w-full text-left";
            const inner = (
              <>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Icon className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-700 flex-shrink-0 transition-colors" />
                  <span className="text-sm font-medium text-[var(--p-ink)] group-hover:text-primary-700">{item.label}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-[var(--p-faint)] group-hover:text-primary-600 flex-shrink-0" />
              </>
            );
            return item.external
              ? <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer" className={rowClass}>{inner}</a>
              : <Link key={item.path} to={item.path} className={rowClass}>{inner}</Link>;
          })}
        </div>
        <div className="px-6 pb-6">
          <Link to="/elevation/lab" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
            <FlaskConical className="h-4 w-4" />
            View All Lab Features
          </Link>
        </div>
      </div>

    </div>
  );
};
